import React, { useState, useEffect, useRef } from "react";
import * as tf from "@tensorflow/tfjs";

const LABELS = ["cardboard", "glass", "metal", "paper", "plastic", "trash"];
const INPUT_SIZE = 224;

export default function ImageClassifier() {
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const imgRef = useRef(null);

  // Load model once
  useEffect(() => {
    const loadModel = async () => {
      try {
        const loaded = await tf.loadGraphModel("/tfjs_model/model.json");
        setModel(loaded);
        console.log("✅ Model loaded");
      } catch (err) {
        console.error("Error loading model:", err);
      } finally {
        setLoading(false);
      }
    };
    loadModel();
  }, []);

  // --- Second-level bin mapping with accessible colors ---
  const getBinCategory = (label) => {
    // Yellow (banana): visible on white background
    const YELLOW = {
      bin: "Yellow bin",
      style: {
        bg: "#FFF8B3",       // light banana
        text: "#3B2900",     // deep brown
        border: "#F4C842",   // saturated border
        accent: "#F4C842",   // accent bar
      },
      icon: "🟡",
    };

    // Green bin: recyclable materials
    const GREEN = {
      bin: "Green bin",
      style: {
        bg: "#E8F5E9",
        text: "#1B5E20",
        border: "#66BB6A",
        accent: "#43A047",
      },
      icon: "🟢",
    };

    // Trash bin
    const TRASH = {
      bin: "Trash bin",
      style: {
        bg: "#EFEBE9",
        text: "#3E2723",
        border: "#8D6E63",
        accent: "#795548",
      },
      icon: "🗑️",
    };

    if (["cardboard", "paper"].includes(label)) return { ...YELLOW, label };
    if (["glass", "metal", "plastic"].includes(label)) return { ...GREEN, label };
    if (label === "trash") return { ...TRASH, label };

    return {
      bin: "Unknown bin",
      label,
      style: { bg: "#F5F5F5", text: "#212121", border: "#BDBDBD", accent: "#9E9E9E" },
      icon: "❓",
    };
  };

  // --- Predict top class ---
  const predict = async (imgEl) => {
    if (!model) return;
    
    try {
      const x = tf.tidy(() =>
        tf.browser
          .fromPixels(imgEl)
          .resizeBilinear([INPUT_SIZE, INPUT_SIZE])
          .toFloat()
          .expandDims(0)
      );
      const y = model.execute(x);
      const probs = tf.softmax(y);
      const data = Array.from(await probs.data());
      tf.dispose([x, y, probs]);

      const topIndex = data.indexOf(Math.max(...data));
      const label = LABELS[topIndex] || `class_${topIndex}`;
      const prob = data[topIndex];

      const { bin, style, icon } = getBinCategory(label);
      setResult({ label, prob, bin, style, icon });
    } finally {
      setProcessing(false);
    }
  };

  // --- Handle upload ---
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !model) return;
    
    setResult(null); // Clear previous result
    setProcessing(true); // Show spinner immediately
    
    const img = imgRef.current;
    img.src = URL.createObjectURL(file);
    img.onload = () => predict(img);
  };

  return (
    <div style={{ maxWidth: 640, margin: "24px auto", textAlign: "center" }}>
      {loading && (
        <div style={{ padding: "40px 20px", color: "#666" }}>
          <div
            style={{
              display: "inline-block",
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #4CAF50",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: "16px",
            }}
          />
          <p>Loading model…</p>
        </div>
      )}
      {!loading && (
        <>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ margin: "16px 0" }}
            disabled={processing}
          />
          <div style={{ marginTop: 20 }}>
            <img
              ref={imgRef}
              alt=""
              style={{
                display: result || processing ? "block" : "none",
                maxWidth: 280,
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,.1)",
                margin: "0 auto",
              }}
            />
            {processing && (
              <div style={{ padding: "40px 20px", color: "#666", marginTop: "20px" }}>
                <div
                  style={{
                    display: "inline-block",
                    width: "40px",
                    height: "40px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #4CAF50",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "16px",
                  }}
                />
                <p>Analyzing image...</p>
              </div>
            )}
            {result && !processing && (
              <div
                style={{
                  marginTop: 24,
                  padding: "14px 20px 14px 16px",
                  borderRadius: 10,
                  background: result.style.bg,
                  color: result.style.text,
                  border: `1px solid ${result.style.border}`,
                  display: "inline-block",
                  fontSize: 18,
                  fontWeight: 600,
                  boxShadow: `inset 6px 0 0 ${result.style.accent}, 0 2px 8px rgba(0,0,0,.06)`,
                }}
              >
                {result.icon} Uploaded image is predicted as <b>{result.label}</b> and
                should go to <b>{result.bin}</b>.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
